import Backbone from 'backbone';

import Config from '../../config'
import Model from '../model';
import Stickers from '../../collection/refbooks/stickers';

import { fmt, unixts, parseYYYYMMDD } from '../../utils/date';
import { eq, eqOrdered, hashify } from '../../utils/arrays';
import { filterAvailableStickers } from './field.utils';





export default Model.extend( {

    endpoint: 'fields', responseAt: 'custom_field',
    
    defaults() {
        return {
            // "id" must be specified here in order
            // to make Epoxy's "toggle:not(id)" binding filter work.
            // (see customField.form.handlebars and "format"
            // field for example).
            id: undefined,
            system: 'f',
            cluster: null,
            table_id: undefined, multiselect: undefined, allow_add_num: undefined,
            name: '',
            format: 'string',
            group_name: undefined,
            possible_values: [],
            regexp: undefined,
            min_length: undefined,
            max_length: undefined,
            is_required: 'f',
            default_value: undefined,
            editable_by_default: 't',
            editable_inverted_for_roles: [],
            device_gallery_enabled_by_default: null,
            device_gallery_enabled_inverted_for_roles: null,
            visible: 't',
            is_for_all: 'f',
            bindings: [],
            min_count: 1,
            max_count: 1,
            min_size: undefined,
            max_size: undefined,
            auto_apply_sticker: 'f',
            subfields: [],
            file_semantics: ['PHOTO'],
            sticker_ids: null, __all_stickers_available: undefined,
        }
    },

    computeds: {
        
        // make copy of "regexp" property
        // with start and end "/"
        regexp_: {
            
            set(value) {
                if ('string' !== typeof value)
                    return { regexp: value };
                    
                if ('' === value)
                    return { regexp: '' };
                    
                return { regexp: `/${value}/` };
            },
            
            get() {
                let value = this.get( 'regexp' );
                // "/value/"
                if ('string' === typeof value)
                    // correct for empty string too
                    value = value.substring( 1, value.length - 1 ); 
                return value;
            }
        },
        
        // treat "0" for minlength and maxlength as no value
        min_length_: {
            
            set(value) {
                if ('' === value)
                    return { min_length: 0 };
                return { min_length: value }; 
            },
            
            get() {
                let value = this.get( 'min_length' );
                if ('0' === value)
                    return undefined; 
                if (0 === value) 
                    return undefined;
                    
                return value;
            }
            
        },

        // treat "0" for minlength and maxlength as no value
        max_length_: {
            
            set(value) {
                if ('' === value)
                    return { max_length: 0 };
                return { max_length: value }; 
            },
            
            get() {
                let value = this.get( 'max_length' );
                if ('0' === value)
                    return undefined; 
                if (0 === value) 
                    return undefined;
                    
                return value;
            }
            
        },
        
        
        // treat list of strings as string with "\n"s
        // at UI level
        possible_values_: {

            set(value) {
                const newPossibleValues = value.split( '\n' );

                const defaultValue = this.get( 'default_value' );
                if (!defaultValue)
                    return { possible_values: newPossibleValues }; 

                // default value hasn't changed in possible values list
                if (~newPossibleValues.indexOf( defaultValue ))
                    return { possible_values: newPossibleValues }; 

                const oldPossibleValues = this.get( 'possible_values' ) || [];
                if (newPossibleValues.length !== oldPossibleValues.length) 
                    return { possible_values: newPossibleValues, default_value: '' }; 
                
                const oldDefaultValueIndex = oldPossibleValues.indexOf( defaultValue );
                if (oldDefaultValueIndex < 0)
                    return { possible_values: newPossibleValues, default_value: '' }; 

                const changedItems = newPossibleValues
                    .filter( (value, index ) => value !== oldPossibleValues[index] );
                // no changes or more than one row has been changed:
                // cannot find match for old default value in changed rows
                if (!changedItems.length || changedItems.length > 1)
                    return { possible_values: newPossibleValues, default_value: '' }; 

                // * lengths are equal
                // * one change 
                // * defval is in old list
                // * defval is not in new list
                // -> take new list in position of change
                return { possible_values: newPossibleValues, default_value: changedItems[0] }; 
            },
            
            get() {
                let value = this.get( 'possible_values' );
                if (value instanceof Array)
                    value = value.join( '\n' );
                    
                return value;
            }
            
        },

        default_datetime: {

            set(value) {
                if (this.get( 'format') !== 'date')
                    return {};

                return { default_value: value };
            },

            get() {
                if (this.get( 'format') !== 'date')
                    return null;

                const value = this.get( 'default_value' );
                if (!value)
                    return null;

                return value;
            }

        },

        // for converting timestamp (as in binder) to string (as in model/server)
        default_date: {

            set(value) {
                if (this.get( 'format') !== 'date-only')
                    return {};

                return { default_value: fmt( value, 'YYYY-MM-DD' ) };
            },

            get() {
                if (this.get( 'format') !== 'date-only')
                    return null;

                const value = this.get( 'default_value' );
                if (!value)
                    return null;

                // no need in fake "00:00:00" string
                // because this timestamp will be converted to date string next
                return unixts( parseYYYYMMDD( value ) );
            }

        },

        default_possible_value: {

            set(value) {
                return { default_value: value };
            },

            get() {
                return {
                    selected: this.get( 'default_value' ),
                    options: this.get( 'possible_values' )
                };
            },
        },

    },
    
    customParser(payload) {
        if (payload.subfields instanceof Array)
            payload.subfields = payload.subfields.map( 
                subfieldPayload => this.customParser( subfieldPayload ) );

        if ('string' === typeof payload.possible_values)
            payload.possible_values = payload.possible_values.split( '\n' );
        if ('' === payload.regexp)
            payload.regexp = undefined;
        if (payload.order)
            payload.order = +payload.order;

        const { news_type_ids, ...json } = payload;
        return { ...json };
    },

    clone() {
        const clonify = ({ subfields, ...json }) => ({
            ...json,
            subfields: subfields.map( clonify )
        });

        const Class = this.constructor;
        return new Class( clonify( this.toJSON() ) );
    },

    diff(another, options = {}) {
        const diff = Model.prototype.diff.call( this, another, options )
            .filter( ({ attrName }) => attrName !== 'bindings');

        const oldBindings = another.get( 'bindings' );
        const newBindings = this.get( 'bindings' );

        const normalizeBindings = (bindings, is_for_all, editable_by_default, editable_inverted_for_roles) =>
            bindings
                .map( normalizeBinding )
                // биндинг остаётся, если он 
                // a) старый
                // b) включённый
                // c) выключенный, но поле для всех и есть правила
                // d) есть master_field
                // ***
                // все 't'/'f' уже сконвертированы в true/false в normalizeBinding
                .filter( binding => 
                    binding.id ||
                    binding.on ||
                    binding.master_field || 
                    !binding.on &&
                    [true, 't'].includes( is_for_all ) &&
                    binding.rules.some( rule =>
                        rule.id ||
                        rule.visible_by_default ||
                        rule.visible_inverted_for_roles.length > 0 ||
                        null != rule.editable_by_default && (rule.editable_by_default != [true, 't'].includes( editable_by_default )) ||
                        null != rule.editable_inverted_for_roles && !eq( rule.editable_inverted_for_roles, editable_inverted_for_roles ) ) )
                .sort( (bindingA, bindingB) => bindingA.type.id - bindingB.type.id );

        const originalBindings = normalizeBindings( oldBindings, 
            another.get( 'is_for_all' ),
            another.get( 'editable_by_default' ),
            another.get( 'editable_inverted_for_roles' ) );
        const fieldBindings = normalizeBindings( newBindings, 
            this.get( 'is_for_all' ),
            this.get( 'editable_by_default' ),
            this.get( 'editable_inverted_for_roles' ) );

        if ( JSON.stringify( originalBindings ) !== JSON.stringify( fieldBindings ))
            diff.push( {
                attrName: 'bindings',
                oldValue: oldBindings, newValue: newBindings
            } );

        const oldSubfields = another.get( 'subfields' );
        const newSubfields = this.get( 'subfields' );

        const normalize = array => array
            .sort( ({ order: a }, { order: b }) => a - b )
            // ignore "bindings", "is_for_all", "cluster" for subfields
            .map( json => Object.keys( json )
                .filter( key => key != 'bindings' && key != 'new-order' && key != 'is_for_all' && key != 'cluster' )
                .sort()
                .map( key => [key, json[key]] )
                .map( ([key, value]) => 'subfields' === key
                    ? [key, normalize( [...value] )]
                    : [key, value] )
                .reduce( (hash, [key, value]) => (hash[key] = value, hash), {} ) );

        const originalSubfields = normalize( oldSubfields );
        const fieldSubfields = normalize( newSubfields );

        if (JSON.stringify( originalSubfields ) !== JSON.stringify( fieldSubfields ))
            diff.push( {
                attrName: 'subfields',
                oldValue: oldSubfields, newValue: newSubfields
            } );

        const attrName = 'possible_values';
        if (diff.some( d => d.attrName === attrName ))
            return diff;

        const newValue = this.get( attrName );
        const oldValue = another.get( attrName );
        if (!eqOrdered( this.get( attrName ), another.get( attrName ) ))
            diff.push( { attrName, oldValue, newValue, } );

        return diff;
    },

    suggestionOptions(text) {
        const url = Config.url( `/fields/${this.id}/values?search=${text}` );
        return Backbone.$.ajax( url )
            .then( ({ values }) => values.map( value => value[this.get( 'titleField' )] ) );
    },

    adjusted(originalModel, { types, stickers, roles }) {
        const { ...json } = this.clone().toJSON();
        const Class = this.constructor;
        return Class.adjusted( json, originalModel, { types, stickers, roles } );
    },
    
}, {

    encodeString(decodedString) {
        return encodeURIComponent( decodedString.replaceAll( '.', '[dot]' ).replaceAll( "'", '[apo]' ) );
    },

    decodeString(encodedString) {
        return decodeURIComponent( encodedString ).replaceAll( '[dot]', '.' ).replaceAll( '[apo]', "'" );
    },

    adjusted(json, originalModel, { types, stickers, roles }) {
        const adjusted = adjustAttrsBeforeSave( json,
            json,
            { types, stickers } );

        const Class = this;
        if (!originalModel || !new Class( json ).diff( originalModel ).find( ({ attrName }) => attrName === 'bindings' )) 
            return adjusted;

        const bindings = json.bindings;
        const roleIds = hashify( roles.map( role => role.id ) );

        return {
            ...adjusted, 
            bindings: {
                add: bindings
                    .filter( binding => !binding.id )
                    .filter( binding => 
                        [undefined, true, 't'].includes( binding.on ) ||
                        [true, 't'].includes( json.is_for_all ) ),
                update: bindings
                    .filter( binding => binding.id )
                    .filter( binding => isBindingChanged( binding, originalModel ) )
                    .map( ({ ...binding, rules }) => ({
                        ...binding,
                        rules: rules.map( ({ ...rule, visible_inverted_for_roles }) => ({
                            ...rule,
                            visible_inverted_for_roles: visible_inverted_for_roles
                                .filter( id => roleIds[id] )
                        }) )
                    }) ),
            }
        };
    },

    normalizeBinding(binding) {
        return normalizeBinding( binding );
    }

} );






function isBindingChanged(binding, originalModel) {
    if (!binding.id)
        return true;

    const [otherBinding] = originalModel.get( 'bindings' )
        .filter( otherBinding => otherBinding.id == binding.id );

    return JSON.stringify( normalizeBinding( binding ) ) !== JSON.stringify( normalizeBinding( otherBinding ) );
}

function normalizeBinding({ type, on, master_field, rules }) {
    rules = rules || [];
    if (!rules.length)
        rules = [...rules, { 
            visible_by_default: true, 
            visible_inverted_for_roles: [],
            editable_by_default: null,
            editable_inverted_for_roles: null,
        }]

    return {
        type, 
        on: [true, 't'].includes( on ), 
        master_field: master_field == null ? null : {
            id: +master_field.id,
            blocking: [true, 't'].includes( master_field.blocking ),
        },
        rules: rules.map( attrs => {
            const {
                visible_by_default,
                visible_inverted_for_roles,
                editable_by_default,
                editable_inverted_for_roles,
                ...rule,
            } = attrs;

            return { 
                visible_by_default: 
                    [true, 't'].includes( visible_by_default ),
                visible_inverted_for_roles: 
                    (visible_inverted_for_roles || []).map( id => +id ).sort(),
                editable_by_default: editable_by_default != null
                    ? [true, 't'].includes( editable_by_default )
                    : editable_by_default,
                editable_inverted_for_roles: editable_inverted_for_roles != null
                    ? (editable_inverted_for_roles || []).map( id => +id ).sort()
                    : editable_inverted_for_roles,
                ...rule
            };
        } ),
    };
}

function adjustAttrsBeforeSave(json, topLevelJson, { types, stickers }) {
    const { id, format,
        translit, order, is_required,
        default_value,
        possible_values, 
        min_length, max_length, 
        multiselect, table_id, allow_add_num,
        regexp, 
        min_count, max_count, subfields,
        min_size, max_size, auto_apply_sticker, file_semantics, sticker_ids,
        __all_stickers_available,
        ...attrs } = json;
    
    // remove fake ids
    if (id < 1000000000 && id > 0 || [true, 't'].includes( json.system ))
        attrs.id = id;

    // don't send "format", "translit", "order" fields to the server - those are unchangeable.
    // Don't clear "id" field: if cleared, will force
    // POST-request instead of PUT.
    if (!attrs.id)
        attrs.format = format;

    // possible_values is possible only for "list"                
    if ('list' === format)
        attrs.possible_values = possible_values;

    // min_length/max_length is possible only for "string", "text"
    if (~['string', 'text'].indexOf( format ))
        attrs.min_length = min_length,
        attrs.max_length = max_length;

    // regexp is possible only for "string", "text", "int", "float"                
    if (~['string', 'text', 'int', 'float'].indexOf( format ))
        attrs.regexp = regexp;

    if ('dataTableObjects' === format)
        attrs.multiselect = multiselect,
        attrs.allow_add_num = allow_add_num,
        attrs.table_id = table_id;

    if ('composite' === format || 'file' === format)
        attrs.min_count = min_count,
        attrs.max_count = max_count;

    if ('file' === format)
        attrs.min_size = min_size,
        attrs.max_size = max_size,
        attrs.auto_apply_sticker = auto_apply_sticker,
        attrs.file_semantics = file_semantics,
        attrs.sticker_ids = sticker_ids == null || __all_stickers_available
            ? null
            : filterStickerIds( sticker_ids, topLevelJson, { types, stickers } );
    else
        attrs.min_size = attrs.max_size = attrs.auto_apply_sticker = attrs.file_semantics = attrs.sticker_ids =
            undefined;

    if ('composite' === format)
        // is_for_all, types, bindings, group_name are for top-level fields only
        attrs.subfields = subfields
            .sort( ({ order: a }, { order: b }) => a - b )
            .map( ({ is_for_all, news_type_ids, bindings, group_name, order, ...others }) => 
                adjustAttrsBeforeSave( 
                    {
                        ...others,
                        cluster: attrs.cluster // copy parent cluster
                    },
                    topLevelJson,
                    { types, stickers } ) );
    else
        // required, defaults is for non-composites only
        attrs.is_required = is_required,
        attrs.default_value = default_value;
    
    return attrs;
}

function filterStickerIds(stickerIds, field, { types, stickers }) {
    if (stickerIds == null)
        return stickerIds;

    const ids = hashify( stickerIds );
    const hereStickers = new Stickers( stickers
        .models
        .filter( stickerModel => ids[stickerModel.id] ) )

    return filterAvailableStickers( hereStickers, field, { types } )
        .models
        .map( sticker => sticker.id );
}




// WEBPACK FOOTER //
// ./src/js/model/refbooks/field.js